import moment from 'moment'
import type { CSSProperties, ReactElement } from 'react'
import { Button } from 'app/components/ui/button'
import { cn } from 'app/lib/utils'
import type { AppointmentsCalendarState } from './useAppointmentsCalendar'

const WEEKDAYS = [`Mon`, `Tue`, `Wed`, `Thu`, `Fri`, `Sat`, `Sun`]

/** How full each day is, not who is in it. A day opens on the day board. */
export function CalendarMonth({ calendar }: { calendar: AppointmentsCalendarState }): ReactElement {
  const month = moment(calendar.day).month()
  const weeks = calendar.range.days.length / 7

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-default bg-surface">
      <div className="grid shrink-0 grid-cols-7 border-b border-default">
        {WEEKDAYS.map((weekday) => (
          <div key={weekday} className="px-2 py-1.5 text-body-xs font-medium text-subtle">
            {weekday}
          </div>
        ))}
      </div>
      <div
        className="grid min-h-0 flex-1 grid-cols-7 grid-rows-(--month-rows)"
        style={{ '--month-rows': `repeat(${weeks}, minmax(4rem, 1fr))` } as CSSProperties}
      >
        {calendar.range.days.map((day, index) => {
          const count = calendar.counts.get(day)
          const inMonth = moment(day).month() === month
          const isToday = day === calendar.today
          return (
            <div
              key={day}
              className={cn(`min-w-0 border-default`, index % 7 > 0 && `border-l`, index >= 7 && `border-t`)}
            >
              <Button
                type="button"
                variant="ghost"
                size="inline"
                title="Open this day"
                onClick={() => calendar.goTo(day, `day`)}
                className="size-full items-stretch justify-stretch overflow-hidden"
              >
                <span className="flex min-w-0 flex-1 flex-col items-start gap-1 p-1.5 text-left md:p-2">
                  <span
                    className={cn(
                      `rounded px-1 text-body-sm font-medium tabular-nums`,
                      inMonth ? `text-strong` : `text-minimal`,
                      isToday && `bg-inverse text-inverse`,
                    )}
                  >
                    {moment(day).format(`D`)}
                  </span>
                  {count && count.booked > 0 && (
                    <span className={cn(`px-1 text-body-sm tabular-nums`, inMonth ? `text-default` : `text-minimal`)}>
                      <span className="font-semibold text-strong">{count.booked}</span>
                      <span className="max-md:hidden"> {count.booked === 1 ? `visit` : `visits`}</span>
                    </span>
                  )}
                  {count && count.calledOff > 0 && (
                    <span className="px-1 text-body-xs tabular-nums text-subtle max-md:hidden">
                      {count.calledOff} cancelled or broken
                    </span>
                  )}
                </span>
              </Button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
